import React, { memo, useCallback } from 'react';

import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import localization from '../../../../shared/strings';
import { selectAggregatedProductCoreFields } from '../../../../store/selectors/aggregatedProductDetailsSelectors';
import { AGGREGATED_FIELD_STATE } from '../../../../store/products/helpers/aggregatedFieldState';
import DetailsBlock from '../../../DetailsBlock';
import SeoSection from '../../SeoSection/SeoSection';
import { stripHtmlToPlainText } from '../../SeoSection/helpers/stripHtmlToPlainText';
import AggregatedScalarField from '../fields/AggregatedScalarField';
import { aggregatedSeoFieldsPropType } from '../helpers/aggregatedFieldPropTypes';
import {
  getAggregatedCommonString,
  hasAggregatedMultipleFields,
  isAggregatedIdentityFieldEditable,
} from '../helpers/aggregatedFieldHelpers';
import { mapAggregatedFieldToInputProps } from '../helpers/mapAggregatedFieldToInputProps';
import { resolveAggregatedFieldTooltip } from '../helpers/resolveAggregatedFieldTooltip';
import { useBindAggregatedField } from '../hooks/useBindAggregatedField';

const l10n = localization.PRODUCT_INFO;

const AggregatedSeoSection = ({
  readOnly,
  readOnlyTooltip,
  seoFields,
  detailsPanelVisibility,
  toggleVisibility,
  blockName,
  blockTitle,
}) => {
  const productCoreFields = useSelector(selectAggregatedProductCoreFields);

  const seoTitleBindings = useBindAggregatedField('seoTitle', seoFields?.seoTitle, l10n.seo.seoTitle);
  const seoDescriptionBindings = useBindAggregatedField(
    'seoDescription',
    seoFields?.seoDescription,
    l10n.seo.seoDescription,
  );
  const handleBindings = useBindAggregatedField(
    'handle',
    seoFields?.handle,
    l10n.autoMappedFieldLabels.handle,
  );

  const useNativeSeo = !hasAggregatedMultipleFields([
    seoFields?.handle,
    seoFields?.seoTitle,
    seoFields?.seoDescription,
  ]);

  const fallbackTitle = productCoreFields?.title?.state === AGGREGATED_FIELD_STATE.COMMON
    ? (productCoreFields.title.value ?? '')
    : '';
  const fallbackDescription = productCoreFields?.descriptionHtml?.state === AGGREGATED_FIELD_STATE.COMMON
    ? stripHtmlToPlainText(productCoreFields.descriptionHtml.value ?? '')
    : '';

  const onChangeSeoTitle = useCallback((value) => {
    seoTitleBindings.onChange(value);
  }, [seoTitleBindings]);

  const onChangeSeoDescription = useCallback((value) => {
    seoDescriptionBindings.onChange(value);
  }, [seoDescriptionBindings]);

  const onChangeHandle = useCallback((value) => {
    handleBindings.onChange(value);
  }, [handleBindings]);

  if (!seoFields) return null;

  const handleReadOnly = readOnly || !isAggregatedIdentityFieldEditable(seoFields.handle);
  const handleTooltip = resolveAggregatedFieldTooltip({
    field: seoFields.handle,
    layerReadOnly: readOnly,
    layerReadOnlyTooltip: readOnlyTooltip,
    isIdentity: true,
    canSet: !handleReadOnly,
    canClear: false,
  });
  const seoTitleTooltip = resolveAggregatedFieldTooltip({
    field: seoFields.seoTitle,
    layerReadOnly: readOnly,
    layerReadOnlyTooltip: readOnlyTooltip,
    canSet: !readOnly,
    canClear: !readOnly,
  });
  const seoDescriptionTooltip = resolveAggregatedFieldTooltip({
    field: seoFields.seoDescription,
    layerReadOnly: readOnly,
    layerReadOnlyTooltip: readOnlyTooltip,
    canSet: !readOnly,
    canClear: !readOnly,
  });

  if (useNativeSeo) {
    return (
      <SeoSection
        handle={getAggregatedCommonString(seoFields.handle, '')}
        seoTitle={getAggregatedCommonString(seoFields.seoTitle, '')}
        seoDescription={getAggregatedCommonString(seoFields.seoDescription, '')}
        fallbackTitle={fallbackTitle}
        fallbackDescription={fallbackDescription}
        readOnly={readOnly}
        readOnlyTooltip={readOnlyTooltip}
        detailsPanelVisibility={detailsPanelVisibility}
        toggleVisibility={toggleVisibility}
        blockName={blockName}
        blockTitle={blockTitle}
        handleReadOnly={handleReadOnly}
        handleTooltip={handleTooltip}
        seoTitleTooltip={seoTitleTooltip}
        seoDescriptionTooltip={seoDescriptionTooltip}
        onChangeHandle={handleReadOnly ? undefined : onChangeHandle}
        onChangeSeoTitle={readOnly ? undefined : onChangeSeoTitle}
        onChangeSeoDescription={readOnly ? undefined : onChangeSeoDescription}
      />
    );
  }

  const handleField = mapAggregatedFieldToInputProps(seoFields.handle);
  const seoTitleField = mapAggregatedFieldToInputProps(seoFields.seoTitle);
  const seoDescriptionField = mapAggregatedFieldToInputProps(seoFields.seoDescription);
  const handleCanClear = !handleReadOnly && Boolean(handleField.value || handleField.isMultiple);
  const seoTitleCanClear = !readOnly && Boolean(seoTitleField.value || seoTitleField.isMultiple);
  const seoDescriptionCanClear = !readOnly && Boolean(
    seoDescriptionField.value || seoDescriptionField.isMultiple,
  );

  return (
    <DetailsBlock
      dataQa="aggregatedProductSeo"
      detailsPanelVisibility={detailsPanelVisibility}
      toggleVisibility={toggleVisibility}
      blockName={blockName}
      blockTitle={blockTitle}
    >
      <AggregatedScalarField
        label={l10n.autoMappedFieldLabels.handle}
        value={handleField.value}
        placeholder={handleField.placeholder}
        readOnly={handleReadOnly || handleField.readOnly}
        tooltip={resolveAggregatedFieldTooltip({
          field: seoFields.handle,
          layerReadOnly: readOnly,
          layerReadOnlyTooltip: readOnlyTooltip,
          isIdentity: true,
          canSet: !handleReadOnly,
          canClear: handleCanClear,
        })}
        showClearButton={handleCanClear}
        onChange={handleReadOnly ? undefined : handleBindings.onChange}
        onClear={handleReadOnly ? undefined : handleBindings.onClear}
      />
      <AggregatedScalarField
        label={l10n.seo.seoTitle}
        value={seoTitleField.value}
        placeholder={seoTitleField.placeholder}
        readOnly={readOnly || seoTitleField.readOnly}
        tooltip={resolveAggregatedFieldTooltip({
          field: seoFields.seoTitle,
          layerReadOnly: readOnly,
          layerReadOnlyTooltip: readOnlyTooltip,
          canSet: !readOnly && !seoTitleField.readOnly,
          canClear: seoTitleCanClear,
        })}
        showClearButton={seoTitleCanClear}
        onChange={readOnly ? undefined : seoTitleBindings.onChange}
        onClear={readOnly ? undefined : seoTitleBindings.onClear}
      />
      <AggregatedScalarField
        multiline
        label={l10n.seo.seoDescription}
        value={seoDescriptionField.value}
        placeholder={seoDescriptionField.placeholder}
        readOnly={readOnly || seoDescriptionField.readOnly}
        tooltip={resolveAggregatedFieldTooltip({
          field: seoFields.seoDescription,
          layerReadOnly: readOnly,
          layerReadOnlyTooltip: readOnlyTooltip,
          canSet: !readOnly && !seoDescriptionField.readOnly,
          canClear: seoDescriptionCanClear,
        })}
        showClearButton={seoDescriptionCanClear}
        onChange={readOnly ? undefined : seoDescriptionBindings.onChange}
        onClear={readOnly ? undefined : seoDescriptionBindings.onClear}
      />
    </DetailsBlock>
  );
};

AggregatedSeoSection.propTypes = {
  readOnly: PropTypes.bool,
  readOnlyTooltip: PropTypes.string,
  seoFields: aggregatedSeoFieldsPropType,
  detailsPanelVisibility: PropTypes.objectOf(PropTypes.bool).isRequired,
  toggleVisibility: PropTypes.func.isRequired,
  blockName: PropTypes.string.isRequired,
  blockTitle: PropTypes.node.isRequired,
};

AggregatedSeoSection.defaultProps = {
  readOnly: false,
  readOnlyTooltip: null,
  seoFields: null,
};

export default memo(AggregatedSeoSection);
